//Built-in libraries
import { useEffect, useState } from 'react';

//Third party libraries
import { useFormContext } from 'react-hook-form';

//Custom components
import HookMaskedInput from 'components/UI/HookForm/Form/MaskedInput';
import { HookTextarea } from 'components/UI/HookForm/Form/TextArea';
import { HookSelect } from 'components/UI/HookForm/Form/Select';
import { HookRadioGroup } from 'components/UI/HookForm/Form/RadioGroup';
import Button from 'components/UI/Button';

//Services
import { useApplicationFormContext } from 'features/ApplicationForm/ApplicationFormProvider';

//STP Libraries

//Assets and Miscellaneous
import JobClassification from './JobClassifications';
import { priorityDevelopmentAreas } from './constants';

//Component Begin

export default function EconomicOverview() {
  const [formError, setFormError] = useState({
    jobClassification: '',
  });
  const { setCurrentStep, data, updateData } = useApplicationFormContext();
  const {
    watch,
    getValues,
    trigger,
    formState: { isValid },
  } = useFormContext();

  const createNewJob = watch('step4.section1.createNewJob');
  const willResultRhu = watch('step4.section1.willResultRhu');
  const affordableHousing = watch('step4.section1.affordableHousing');
  const advDisadvantagedBusiness = watch(
    'step4.section4.advDisadvantagedBusiness'
  );
  const { step4 } = getValues();

  useEffect(() => {
    if (data.step4.section1.jobClassification.length > 0) {
      setFormError((prev) => {
        return { ...prev, jobClassification: '' };
      });
    }
  }, [data]);

  const nextHandler = () => {
    trigger();
    if (!isValid) return;

    if (createNewJob === 'yes' && data.step4.section1.jobClassification.length === 0) {
      setFormError({ jobClassification: 'At least one is required' });
    } else {
      updateData({
        step4: {
          ...step4,
          section1: {
            ...step4?.section1,
            jobClassification: data?.step4?.section1.jobClassification,
          },
        },
      });
      setCurrentStep((prevState) => prevState + 1);
    }
  };

  return (
    <div className='sm:rounded-r-md sm:overflow-hidden'>
      <div className='py-6 px-4 space-y-6 sm:p-6'>
        <h3 className='text-lg leading-6 font-medium text-green-500'>
          Economic Overview
        </h3>
        <div className='flex flex-col gap-10'>
          <section className='space-y-4'>
            <h3>Job Creation/Retention</h3>
            <div className='grid grid-cols-6 gap-4'>
              <HookRadioGroup
                className='col-span-6 md:col-span-6'
                label='Upon completion of the project, will new jobs be created?'
                name='step4.section1.createNewJob'
                id='create-new-job'
                options={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
              />
              {createNewJob === 'yes' && (
                <JobClassification formValues={step4} />
              )}
              {formError.jobClassification.length > 0 && (
                <p className='text-tertiaryColor-normal'>
                  {formError.jobClassification}
                </p>
              )}
              <HookRadioGroup
                className='col-span-6 md:col-span-6'
                label='Will this project result in residential housing units?'
                name='step4.section1.willResultRhu'
                id='step4-will-results-rhu'
                options={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
              />
              {willResultRhu === 'yes' && (
                <div className='col-span-1'>
                  <HookMaskedInput
                    id='step4-rhuCount'
                    label='How many?'
                    name='step4.section1.rhuCount'
                    maskedComponent='numeric'
                  />
                </div>
              )}
              <HookRadioGroup
                className='col-span-6 md:col-span-6'
                label='Will any be affordable housing?'
                name='step4.section1.affordableHousing'
                id='step4-will-affordable-housing'
                options={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
              />
              {affordableHousing === 'yes' && (
                <div className='col-span-1'>
                  <HookMaskedInput
                    id='step4-affordable-housing-count'
                    label='How many?'
                    name='step4.section1.ahCount'
                    maskedComponent='numeric'
                  />
                </div>
              )}
            </div>
          </section>
          <section className='space-y-4'>
            <h3>Building Information</h3>
            <div className='grid grid-cols-6 gap-4'>
              <div className='col-span-6'>
                <HookTextarea
                  label='Please describe any private investment committed to this project'
                  name='step4.section2.description1'
                  placeholder='Describe private investment committed'
                  rows={5}
                />
              </div>
              <div className='col-span-6'>
                <HookTextarea
                  label='Please describe complete scope of work that private investment will be applied to:'
                  name='step4.section2.description2'
                  placeholder='Describe complete scope of work'
                  rows={5}
                />
              </div>
              <div className='col-span-6'>
                <HookTextarea
                  label='In addition to Façade, what other investment is involved?'
                  name='step4.section2.description3'
                  placeholder='Describe other involved investment'
                  rows={5}
                />
              </div>
            </div>
          </section>
          <section className='space-y-4'>
            <h3>Building Location/Condition</h3>
            <div className='grid grid-cols-6 gap-4'>
              <HookRadioGroup
                className='col-span-6 md:col-span-6'
                label='Is this building previously vacant and being returned to use?'
                name='step4.section3.q1'
                id='step4-section3-q1'
                options={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
              />
              <HookRadioGroup
                className='col-span-6 md:col-span-6'
                label='Is the property severely deteriorated with a negative impact on the community?'
                name='step4.section3.q2'
                id='step4-section3-q2'
                options={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
              />
              <HookRadioGroup
                className='col-span-6 md:col-span-6'
                label='Is the property identified as blighted?'
                name='step4.section3.q3'
                id='step4-section3-q3'
                options={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
              />
              <div className='col-span-6 md:col-span-6'>
                <HookSelect
                  customClassName='w-1/2'
                  label='Please select the priority development area this property is located in (if applicable)'
                  id='step4-section3-q4'
                  name='step4.section3.q4'
                  options={priorityDevelopmentAreas.map((area) => ({
                    label: area,
                    value: area,
                  }))}
                />
              </div>
              <HookRadioGroup
                className='col-span-6 md:col-span-6'
                label='Is this property in a prominent, highly visible location or located on a major arterial highway?'
                name='step4.section3.q5'
                id='step4-section3-q5'
                options={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
              />
            </div>
          </section>
          <section className='space-y-4'>
            <h3>Disadvantaged Business</h3>
            <div className='grid grid-cols-6 gap-4'>
              <HookRadioGroup
                className='col-span-6 md:col-span-6'
                label='Will this project advance disadvantaged businesses, defined as minority or woman owned business, or small business?'
                name='step4.section4.advDisadvantagedBusiness'
                id='step4-section4-adv-disadvantaged-business'
                options={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
              />
              {advDisadvantagedBusiness === 'yes' && (
                <div className='col-span-6'>
                  <HookTextarea
                    label='Explain the disadvantaged businesses'
                    name='step4.section4.description'
                    rows={5}
                  />
                </div>
              )}
            </div>
          </section>
        </div>

        <div className='flex gap-4 justify-end mt-5'>
          <Button
            label='Previous'
            btnStyle='neutral'
            type='button'
            onClick={() => setCurrentStep((prev) => prev - 1)}
          />
          <Button
            label='Next'
            btnStyle='primary'
            type='button'
            onClick={nextHandler}
          />
        </div>
      </div>
    </div>
  );
}
